import _escapeRegExp from 'lodash/escapeRegExp'

export const makeRegex = (
  type: string,
  matchList: string[],
  excludeList: string[]
): RegExp => {
  // the whole name must equal one of the match strings, e.g. a plain
  // "United Healthcare" but not "United Healthcare | Optum"
  if (type === 'exact') {
    const orSeparatedMatchList = matchList.map(_escapeRegExp).join('|')

    return new RegExp(`^\\s*(${orSeparatedMatchList})\\s*$`, 'i')
  }

  if (type === 'multistring') {
    const orSeparatedMatchList = matchList.map(_escapeRegExp).join('|')
    let multiStringRegex = new RegExp(`(${orSeparatedMatchList})\\b`, 'i')

    if (excludeList !== null) {
      const orSeparatedExcludeList = excludeList.map(_escapeRegExp).join('|')
      multiStringRegex = new RegExp(
        `^(?!.*(${orSeparatedExcludeList})\\b).*(${orSeparatedMatchList})\\b`,
        'i'
      )
    }

    return multiStringRegex
  }
}

export type InsuranceMatcherGroup = {
  matcher: {
    match: string[]
    exclude: string[] | null
    // match whole names only instead of any name containing a match string
    isExactMatch?: boolean
  }
  alias: string | null
  // Exact names of the carrier's parent plan. The pill uses the alias only when
  // the provider accepts the parent plan; otherwise it keeps the name of the
  // first plan in the group (the selected one, else the first alphabetically)
  parentNames?: string[]
  isGrouped: boolean | null
  idToBeHighlighted: string | null
}

export const matcherList: InsuranceMatcherGroup[] = [
  {
    matcher: {
      match: ['Anthem'],
      exclude: null,
    },
    alias: 'Anthem',
    isGrouped: null,
    idToBeHighlighted: null,
  },
  {
    matcher: {
      match: ['BCBS', 'Blue Cross', 'BlueCross', 'Blue Shield', 'BlueShield'],
      exclude: ['Anthem'],
    },
    alias: 'Blue Cross Blue Shield',
    isGrouped: null,
    idToBeHighlighted: null,
  },
]

export const topRatedMatcherList = [
  'Aetna',
  'Cigna',
  'BCBS',
  'Blue Cross',
  'BlueCross',
  'Blue Shield',
  'BlueShield',
  'BlueSheild', // cover a typo
  'Optum',
]

export type InsuranceMatcherConfig = {
  matcherList: InsuranceMatcherGroup[]
  topRatedMatcherList: string[]
}

export const defaultInsuranceMatcherConfig: InsuranceMatcherConfig = {
  matcherList,
  topRatedMatcherList,
}

/**
 * Each carrier gets one pill. Cigna and United Healthcare/Optum plans, including
 * partner plans such as "Firefly Health Plan | Cigna PPO Network" and
 * "Mass General Brigham (Optum)", share their carrier's pill. A plain
 * "United Healthcare" plan is the exception: it gets a pill of its own, next to
 * the "United Healthcare | Optum" one
 *
 * @shared Used by:
 *   - Provider profile pills: app/ProviderDetails/AcceptedInsurance/
 *   - Search result provider cards: hooks/provider/useProviderListingDetails-v3.tsx (city page cards use their own line)
 *
 * Changes here affect both features. Coordinate updates.
 */
export const carrierInsuranceMatcherConfig: InsuranceMatcherConfig = {
  matcherList: [
    ...matcherList,
    {
      matcher: {
        match: ['Aetna'],
        exclude: null,
      },
      alias: 'Aetna',
      parentNames: ['Aetna'],
      isGrouped: null,
      idToBeHighlighted: null,
    },
    {
      matcher: {
        match: ['Cigna', 'Evernorth'],
        exclude: null,
      },
      alias: 'Cigna | Evernorth',
      parentNames: ['Cigna | Evernorth'],
      isGrouped: null,
      idToBeHighlighted: null,
    },
    // must come before the United Healthcare/Optum group, the first matching group wins
    {
      matcher: {
        match: ['United Healthcare', 'UnitedHealthcare'],
        exclude: null,
        isExactMatch: true,
      },
      alias: 'United Healthcare',
      isGrouped: null,
      idToBeHighlighted: null,
    },
    {
      matcher: {
        match: ['United Healthcare', 'UnitedHealthcare', 'Optum'],
        exclude: null,
      },
      alias: 'United Healthcare | Optum',
      parentNames: [
        'United Healthcare | Optum',
        'UnitedHealthcare | Optum',
        'Optum | United Healthcare',
        'Optum | UnitedHealthcare',
      ],
      isGrouped: null,
      idToBeHighlighted: null,
    },
  ],
  topRatedMatcherList: [
    ...topRatedMatcherList,
    'United Healthcare',
    'UnitedHealthcare',
  ],
}
